import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import logo from '../assets/logo_brandstarter.png';
import claraOuvert from '../assets/clara_ouvert.png';

const css = `
  .au-page { --g:#173B34; --o:#E4572E; --c:#F6F2EC; --line:#E3DCCF; --mut:#6b6b64;
    min-height: 100vh; min-height: 100dvh; background: var(--c); font-family: 'Inter', system-ui, -apple-system, sans-serif;
    color: #222; display: flex; justify-content: center; }
  .au-wrap { width: 100%; max-width: 440px; padding: 20px 22px 28px; display: flex; flex-direction: column; min-height: 100vh; min-height: 100dvh; }
  .au-top { display: flex; align-items: center; justify-content: space-between; min-height: 44px; }
  .au-logo { height: 34px; width: auto; display: block; }
  .au-back { background: none; border: 0; font-size: 24px; color: var(--g); width: 44px; height: 44px; margin-left: -10px; cursor: pointer; }
  .au-step { font-size: 13px; font-weight: 700; color: var(--mut); }
  .au-h { font-family: inherit; font-size: 29px; line-height: 1.12; color: var(--g); font-weight: 800; margin: 26px 0 0; letter-spacing: -0.01em; }
  .au-p { font-size: 15px; line-height: 1.5; color: var(--mut); margin: 10px 0 0; }
  .au-clara { display: flex; gap: 12px; align-items: center; background: #fff; border: 1px solid var(--line); border-radius: 18px; padding: 12px; margin-top: 20px; }
  .au-clara-av { width: 46px; height: 46px; border-radius: 50%; background: var(--g) url(${claraOuvert}) center 18% / 150% no-repeat; border: 2px solid #fff; flex-shrink: 0; }
  .au-clara p { font-size: 14px; line-height: 1.4; margin: 0; }
  .au-form { margin-top: 22px; display: flex; flex-direction: column; }
  .au-label { font-size: 13px; font-weight: 600; color: var(--g); margin: 0 0 6px; display: block; }
  .au-field { position: relative; margin-bottom: 14px; }
  .au-in { width: 100%; height: 54px; border: 1.5px solid var(--line); background: #fff; border-radius: 14px; padding: 0 16px; font-size: 16px; color: #222; font-family: inherit; outline: none; -webkit-appearance: none; appearance: none; }
  .au-in:focus { border-color: var(--g); box-shadow: 0 0 0 3px rgba(23,59,52,0.12); }
  .au-in.pw { padding-right: 92px; }
  .au-show { position: absolute; right: 4px; bottom: 0; height: 54px; padding: 0 14px; background: none; border: 0; color: var(--g); font-weight: 700; font-size: 13px; cursor: pointer; font-family: inherit; }
  .au-forgot { text-align: right; font-size: 13px; font-weight: 600; color: var(--g); margin: -4px 0 0; }
  .au-spacer { flex: 1; min-height: 20px; }
  .au-btn { display: flex; align-items: center; justify-content: center; min-height: 54px; width: 100%; border: 0; border-radius: 14px; background: var(--o); color: #fff; font-size: 16.5px; font-weight: 700; font-family: inherit; cursor: pointer; }
  .au-btn:disabled { opacity: .6; cursor: default; }
  .au-foot { text-align: center; font-size: 14px; color: var(--mut); margin: 16px 0 0; }
  .au-foot a { color: var(--o); font-weight: 700; text-decoration: none; }
  .au-err { background: #fdecea; border: 1px solid #f5c2bb; color: #9b2c1f; border-radius: 12px; padding: 12px 14px; font-size: 14px; line-height: 1.45; margin-bottom: 14px; }
  .au-ok { background: #e6f2ec; border: 1px solid #b9d9c8; color: var(--g); border-radius: 12px; padding: 14px; font-size: 15px; line-height: 1.45; margin-top: 22px; font-weight: 600; }
  .au-bar { height: 8px; border-radius: 99px; background: #e6dfd2; overflow: hidden; margin-top: 12px; }
  .au-bar i { display: block; height: 100%; background: var(--o); border-radius: 99px; transition: width .3s; }
  .au-chk { display: flex; gap: 12px; align-items: flex-start; background: #fff; border: 1.5px solid var(--line); border-radius: 14px; padding: 14px; margin-bottom: 12px; cursor: pointer; }
  .au-chk input { width: 24px; height: 24px; flex-shrink: 0; accent-color: #173B34; margin: 0; }
  .au-chk span { font-size: 14px; line-height: 1.45; color: #333; }
  .au-chk a { color: var(--g); font-weight: 700; text-decoration: underline; }
  .au-badge { background: #fde6de; color: var(--o); font-weight: 700; font-size: 13.5px; border-radius: 12px; padding: 11px 14px; margin: 4px 0 14px; line-height: 1.4; }
  @media (min-width: 768px) {
    .au-wrap { padding-top: 48px; max-width: 480px; justify-content: center; }
    .au-h { font-size: 34px; }
  }
`;

function Shell({ children, onBack, step }) {
  return (
    <>
      <style>{css}</style>
      <div className="au-page">
        <div className="au-wrap">
          <div className="au-top">
            {onBack ? (
              <button type="button" className="au-back" onClick={onBack} aria-label="Retour">←</button>
            ) : (
              <Link to="/"><img className="au-logo" src={logo} alt="BrandStarter" /></Link>
            )}
            {step && <span className="au-step">{step}</span>}
            {onBack && <span style={{ width: 34 }} />}
          </div>
          {children}
        </div>
      </div>
    </>
  );
}

function Field({ label, type = 'text', value, onChange, placeholder, autoComplete, inputMode }) {
  const [show, setShow] = useState(false);
  const isPw = type === 'password';
  return (
    <div className="au-field">
      <label className="au-label">{label}</label>
      <input
        className={`au-in${isPw ? ' pw' : ''}`}
        type={isPw && show ? 'text' : type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        required
      />
      {isPw && (
        <button type="button" className="au-show" onClick={() => setShow(s => !s)}>
          {show ? 'Masquer' : 'Afficher'}
        </button>
      )}
    </div>
  );
}

export function LoginPage() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const { login }               = useAuth();
  const navigate                = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user.isAdmin ? '/admin' : '/parcours');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <h1 className="au-h">Ta marque,<br />construite pas à pas.</h1>
      <p className="au-p">Clara t'accompagne sur ton positionnement, ton persona et ton message. Accès gratuit 20 jours, sur demande.</p>
      <div className="au-clara">
        <div className="au-clara-av" />
        <p>« Content de te revoir. On reprend où tu t'étais arrêté ? »</p>
      </div>
      <form className="au-form" onSubmit={handleSubmit}>
        {error && <div className="au-err">{error}</div>}
        <Field label="Adresse e-mail" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="toi@exemple.fr" autoComplete="email" inputMode="email" />
        <Field label="Mot de passe" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Ton mot de passe" autoComplete="current-password" />
        <div className="au-spacer" />
        <button type="submit" className="au-btn" disabled={loading}>{loading ? 'Connexion...' : 'Me connecter'}</button>
        <p className="au-foot">Pas encore de compte ? <Link to="/inscription">Demander mon accès</Link></p>
      </form>
    </Shell>
  );
}

export function RegisterPage() {
  const [form, setForm]       = useState({ prenom: '', email: '', password: '', stade: '', projet: '', source: 'LinkedIn', website: '' });
  const [formTs]              = useState(() => Date.now());
  const [consent, setConsent] = useState({ cgu: false, rgpd: false });
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [places, setPlaces]   = useState(null);
  const navigate              = useNavigate();

  useEffect(() => { api.places().then(setPlaces).catch(() => {}); }, []);
  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) return setError('Le mot de passe doit faire au moins 8 caractères.');
    if (!form.stade) return setError('Indique où en est ton projet.');
    if (form.projet.trim().length < 15) return setError('Décris ton projet en une ou deux phrases.');
    if (!consent.cgu) return setError("Coche les conditions d'utilisation pour continuer.");
    if (!consent.rgpd) return setError('Coche la politique de confidentialité pour continuer.');
    setLoading(true);
    try {
      await api.register({
        prenom: form.prenom, email: form.email, password: form.password,
        stade: form.stade, projet: form.projet, source: form.source,
        consentCgu: consent.cgu, consentRgpd: consent.rgpd,
        website: form.website, formTs
      });
      setSuccess(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const stadeOpts = ["J'ai une idée", 'Je me lance (moins de 6 mois)', 'Je suis déjà lancé(e) et je veux clarifier ma marque'];
  const sel = { width: '100%', height: 54, border: '1.5px solid #E3DCCF', background: '#fff', borderRadius: 14, padding: '0 14px', fontSize: 16, fontFamily: 'inherit' };

  return (
    <Shell onBack={() => navigate('/connexion')}>
      {success ? (
        <>
          <div className="au-badge" style={{ marginTop: 22 }}>Demande reçue</div>
          <h1 className="au-h" style={{ fontSize: 26, marginTop: 4 }}>Merci {form.prenom}, ta demande est bien enregistrée</h1>
          <p className="au-p">Je valide chaque compte personnellement. Tu reçois ta réponse par e-mail sous 48 h, et tes 20 jours gratuits démarrent à l'activation de ton compte.</p>
          <div className="au-ok" style={{ fontWeight: 500 }}>En attendant, réfléchis à ton idée en une phrase : c'est la première question que Clara te posera.</div>
          <p className="au-p" style={{ fontSize: 13 }}>Rien reçu après 48 h ? Vérifie tes courriers indésirables ou écris à contact@stratvente.com.</p>
          <div className="au-spacer" />
          <button className="au-btn" onClick={() => navigate('/connexion')}>Retour à la connexion</button>
        </>
      ) : (
        <>
          <div className="au-badge" style={{ marginTop: 22 }}>Phase de lancement</div>
          <h1 className="au-h" style={{ fontSize: 26, marginTop: 4 }}>Demande ton accès à BrandStarter</h1>
          <p className="au-p">Pour offrir un accompagnement de qualité, j'ouvre BrandStarter à un nombre limité de créateurs. Je valide chaque demande moi-même, sous 48 h.</p>
          {places && places.restantes !== null && (
            <div style={{ background: '#173B34', color: '#F6F2EC', borderRadius: 14, padding: '14px 16px', marginTop: 16 }}>
              <b style={{ fontSize: 24, color: '#fff' }}>{places.restantes}</b> place{places.restantes > 1 ? 's' : ''} restante{places.restantes > 1 ? 's' : ''} sur {places.max}
              <div style={{ height: 7, background: 'rgba(255,255,255,.2)', borderRadius: 9, marginTop: 9 }}>
                <div style={{ height: '100%', width: `${Math.max(4, 100 - (places.restantes / places.max) * 100)}%`, background: '#E4572E', borderRadius: 9 }} />
              </div>
              {places.restantes === 0 && <div style={{ fontSize: 13, marginTop: 8 }}>Les places sont prises : ta demande sera placée sur liste d'attente.</div>}
            </div>
          )}
          <form className="au-form" onSubmit={submit}>
            {error && <div className="au-err">{error}</div>}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
              <label>Ne pas remplir<input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} /></label>
            </div>
            <Field label="Prénom" value={form.prenom} onChange={set('prenom')} placeholder="Camille" autoComplete="given-name" />
            <Field label="Adresse e-mail" type="email" value={form.email} onChange={set('email')} placeholder="toi@exemple.fr" autoComplete="email" inputMode="email" />
            <Field label="Mot de passe" type="password" value={form.password} onChange={set('password')} placeholder="8 caractères minimum" autoComplete="new-password" />
            <div className="au-field">
              <label className="au-label">Où en est ton projet ?</label>
              <select style={sel} value={form.stade} onChange={set('stade')}>
                <option value="">Choisis une option</option>
                {stadeOpts.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="au-field">
              <label className="au-label">Décris ton projet en une ou deux phrases</label>
              <textarea className="au-in" style={{ height: 96, padding: 14, resize: 'vertical' }} value={form.projet} onChange={set('projet')} placeholder="Ex : je crée des bougies coulées à la main, vendues en ligne." />
            </div>
            <div className="au-field">
              <label className="au-label">Comment as-tu connu BrandStarter ?</label>
              <select style={sel} value={form.source} onChange={set('source')}>
                <option>LinkedIn</option><option>Bouche à oreille</option><option>Autre</option>
              </select>
            </div>
            <label className="au-chk">
              <input type="checkbox" checked={consent.cgu} onChange={e => setConsent(c => ({ ...c, cgu: e.target.checked }))} />
              <span>J'accepte les <a href="/mentions-legales" target="_blank" rel="noreferrer">conditions d'utilisation</a> de BrandStarter by Stratvente.</span>
            </label>
            <label className="au-chk">
              <input type="checkbox" checked={consent.rgpd} onChange={e => setConsent(c => ({ ...c, rgpd: e.target.checked }))} />
              <span>J'ai lu la <a href="/confidentialite" target="_blank" rel="noreferrer">politique de confidentialité</a> et j'accepte que mes données soient traitées par Stratvente (RGPD).</span>
            </label>
            <div className="au-spacer" />
            <button type="submit" className="au-btn" disabled={loading}>{loading ? 'Envoi...' : 'Envoyer ma demande'}</button>
            <p className="au-foot">Ton compte sera activé après validation. Tes 20 jours gratuits démarrent à l'activation, pas avant.</p>
            <p className="au-foot">Déjà inscrit ? <Link to="/connexion">Me connecter</Link></p>
          </form>
        </>
      )}
    </Shell>
  );
}
